<template>
  <div class="samr-oral-instruction-screen">
    <div class="rounded p-3 mb-4 custom-bg">
      <div class="card samr-card">
        <div class="card-body">
          <div class="samr-legend-title mb-3">
            Leyenda
          </div>

          <div class="row align-items-center justify-content-between">
            <div
              v-for="item in levels"
              :key="item.id"
              class="col-auto d-flex align-items-center mb-2"
            >
              <button
                class="rounded-pill py-1 px-3 text-semibold legend-button mr-3"
                :style="badge(item.color)"
                type="button"
                @click="selectLegend(item)"
              >
                {{ item.name }}
              </button>

              <span class="legend-text">
                {{ item.description }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card samr-card">
      <div class="card-header samr-card__header">
        Pantalla de Instrucciones Orales (PIO)
      </div>

      <div class="card-body samr-card__body">
        <div class="form-row align-items-start mb-4">
          <div class="col-12 col-md-2 col-lg-1">
            <label class="samr-label">Errores:</label>
            <validation-provider
              v-if="!isReadOnly"
              v-slot="{ errors }"
              name="Errores"
              rules="validErrorsValue"
            >
              <input
                v-model="model.errors"
                class="form-control w-70"
                :class="{ 'samr-errors-input--invalid': errors[0] }"
                max="5"
                min="0"
                type="number"
                @input="handleErrorsInput($event.target.value)"
              />
              <span v-if="errors[0]" class="samr-error-text mt-1 d-block">
                <i class="fa fa-times-circle" />
                {{ errors[0] }}
              </span>
            </validation-provider>
            <span v-else class="d-block">
              {{ model.errors }}
            </span>
          </div>

          <div class="col-12 col-md-10 col-lg-11 mt-3 mt-md-0">
            <label class="samr-label">Capacidad:</label>
            <div class="samr-capacity mt-1">
              <span
                v-if="currentLevel"
                class="rounded-pill py-1 px-3 text-semibold"
                :style="badge(currentLevel.color)"
              >
                {{ currentLevel.name }}
              </span>
            </div>
          </div>
        </div>

        <div class="form-group mb-0">
          <label class="samr-label">Interpretación:</label>
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            rules="required"
          >
            <custom-text-area
              v-model="model.interpretation"
              :count-classes="['d-block', 'small-counter']"
              custom-error-msg="Es requerido"
              :disabled="isReadOnly"
              :error="errors[0]"
              :maxlength="5000"
              show-char-count
            />
          </validation-provider>
          <span v-else class="d-block mt-1">
            {{ model.interpretation }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDisabilityEvaluationQuestionnaireLevels as getLevels } from '@/modules/vocational-evaluation/api.js';
import Instruments from '@/utils/constants/vocationalEvaluationInstrumentEnum';
import alertMixin from '@/mixins/alert.mixin.js';
import CustomTextArea from 'CustomTextArea';

import { extend } from 'vee-validate';

extend('validErrorsValue', {
  computesRequired: true,
  validate(value) {
    if (value === null || value === undefined || value === '') {
      return {
        valid: false,
        required: true,
      };
    }

    const num = Number(value);

    if (Number.isNaN(num)) {
      return {
        valid: false,
        required: true,
      };
    }

    return {
      valid: num >= 0 && num <= 5,
      required: true,
    };
  },
  message: 'El campo es requerido y debe ser un número entre 0 y 5',
});

export default {
  name: 'SAMROralInstructionScreen',
  components: {
    CustomTextArea,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      levels: [],
    };
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    errorsRules() {
      return {
        validErrorsValue: true,
      };
    },
    currentLevel() {
      const errors = Number(this.model.errors);

      if (
        this.model.errors === null ||
        this.model.errors === '' ||
        Number.isNaN(errors)
      ) {
        return null;
      }

      return (
        this.levels.find(
          (item) => Number(item.min) <= errors && errors <= Number(item.max)
        ) || null
      );
    },
    badge() {
      return (color) => ({
        background: `${color}22`,
        border: `1px solid ${color}`,
        color,
      });
    },
  },
  created() {
    this.getLevels();
  },
  methods: {
    async getLevels() {
      try {
        const { data } = await getLevels(Instruments.SAMPR);
        this.levels = data || [];
      } catch (error) {
        this.showErrorMessage('Error al cargar los niveles de discapacidad');
      }
    },
    handleErrorsInput(value) {
      if (value === '' || value === null) {
        this.model = {
          ...this.model,
          errors: '',
        };
        return;
      }

      let num = Number(value);

      if (Number.isNaN(num)) return;

      if (num > 5) num = 5;
      if (num < 0) num = 0;

      this.model = {
        ...this.model,
        errors: num,
      };
    },
  },
};
</script>

<style scoped>
.text-semibold {
  font-weight: 600;
}

.samr-card {
  border: 1px solid #dddddd;
  border-radius: 8px;
  box-shadow: none;
}

.samr-card__header {
  background: #f7f7f7;
  border-bottom: 1px solid #dddddd;
  font-weight: 600;
  color: #243b3f;
  padding: 0.95rem 1rem;
}

.samr-legend-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #243b3f;
}

.samr-card__body {
  background: #f7f7f7;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
}

.legend-button {
  background-clip: padding-box;
  outline: none;
  margin-right: 0;
  white-space: nowrap;
  flex-shrink: 0;
}

.legend-text {
  font-size: 0.9rem;
  color: #4d5c60;
  white-space: nowrap;
  margin: 0;
}

.samr-label {
  font-size: 0.92rem;
  font-weight: 500;
  color: #243b3f;
  margin-bottom: 0.5rem;
}

.samr-capacity {
  min-height: 38px;
  display: flex;
  align-items: center;
}

.samr-errors-input {
  max-width: 64px;
}

.samr-errors-input:disabled {
  background-color: #fff;
  color: #243b3f;
  opacity: 1;
  cursor: default;
}

.samr-errors-input--invalid {
  background-color: #f7d7da !important;
  color: #ff5b5c !important;
}

.samr-error-text {
  color: #ff5b5c;
  margin-bottom: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.2;
}
.custom-bg {
  background: #f7f7f7;
}
</style>
